// /Booth/Favorite を保持するState
import Dexie, { type EntityTable } from "dexie";
import type { FavoriteBooth } from "~/types/entity/cWebCatalog";

const stateName = "boothFavoritesState";

type BoothFavoritesState = {
  lastUpdated: number | undefined
  /**
   * boothId をキーとしたMap
   */
  favoriteBoothMap: Map<number | undefined, FavoriteBooth>
}

// 永続化用のデータベース形式は独自に構築する
type PersistBoothFavoritesState = {
  lastUpdated: number | undefined
}

// https://dexie.org/docs/Tutorial/Vue#using-typescript
// database仕様を変更したらスキーマバージョンを +1 する
const SCHEMA_VERSION = 2;
// モジュール評価時に useState を呼ぶと、plugins (favoriteSync.client.ts) 経由で静的 import された場合に
// Nuxt インスタンス生成前に評価されて失敗するため、config の取得は呼び出し時まで遅延させる
const getConfig = () => useCirclemsConfig().get();
const getDatabaseName = () => `BoothFavoritesDatabase${getConfig().eventId}`;
const getDexie = () => {
  if (import.meta.client && getConfig().eventId > 0) {
    const db = new Dexie(getDatabaseName()) as Dexie & { // TODO(as-cast): 強制型キャスト。安全な型付けへ置換を検討
      lastUpdated: EntityTable<PersistBoothFavoritesState, "lastUpdated">
      favoriteBooth: EntityTable<FavoriteBooth, "boothId">
    };
    db.version(SCHEMA_VERSION).stores({
      lastUpdated: "lastUpdated",
      favoriteBooth: "boothId, color, memo"
    });
    return db;
  }
  // サーバー側は IndexedDB なしで実装する
  return undefined;
};

export const useBoothFavoritesState = () => {
  const state = useState<BoothFavoritesState | undefined>(stateName);
  const db = getDexie();

  const getLastUpdated = async () => {
    if (db !== undefined) {
      const [lastUpdatedRecord] = await db.lastUpdated.toArray();
      return lastUpdatedRecord?.lastUpdated;
    }
    return undefined;
  };
  const updateLastUpdated = async (lastUpdated: number) => {
    if (db !== undefined) {
      await db.lastUpdated.clear();
      await db.lastUpdated.add({
        lastUpdated: lastUpdated
      });
    }
  };
  /**
   * IndexedDB から全件読み込む (state は更新しない)
   * @returns lastUpdated が未保存なら undefined
   */
  const readFromDb = async (): Promise<BoothFavoritesState | undefined> => {
    if (db === undefined) {
      return undefined;
    }
    const lastUpdated = await getLastUpdated();
    const favoriteBoothList = await db.favoriteBooth.toArray();
    if (lastUpdated === undefined || favoriteBoothList === undefined) {
      return undefined;
    }
    return {
      lastUpdated: lastUpdated,
      favoriteBoothMap: new Map(favoriteBoothList.map(favorite => [favorite.boothId, favorite])),
    };
  };

  return ({
    /**
     * 読み取り専用のリアクティブな state。コンポーネントは computed で参照する
     */
    state: readonly(state),
    /**
     * 全お気に入りを取得する
     */
    getPersisted: async (): Promise<BoothFavoritesState | undefined> => {
      const stateValue = state.value;
      if (stateValue || db === undefined) {
        return stateValue;
      }

      // 以下 State から取得できなかった時に IndexedDB からの取得を試みる
      let exception: unknown | undefined = undefined;
      try {
        const storedValue = await readFromDb();
        if (storedValue !== undefined) {
          state.value = storedValue;
          return storedValue;
        }
      }
      catch (e) {
        exception = e;
      }
      if (exception) {
        await db.delete(); // 例外になったらキャッシュデータベースは削除する
      }
      state.value = undefined;
      return undefined;
    },
    /**
     * 他 Tab の書き込みを取り込むため、state の有無に関係なく IndexedDB から読み直す。
     * 他 Tab が書き込み中の一時的な例外でキャッシュを消さないよう、例外時は何もしない
     */
    reloadFromDb: async (): Promise<void> => {
      try {
        const storedValue = await readFromDb();
        if (storedValue !== undefined) {
          state.value = storedValue;
        }
      }
      catch {
        // 読み直しに失敗しても現在の state を維持する
      }
    },
    /**
     * 全お気に入りを更新する
     */
    setPersisted: async (v: BoothFavoritesState | undefined) => {
      // State の更新
      state.value = v;
      if (db !== undefined) {
        // IndexedDB の更新
        let exception: unknown | undefined = undefined;
        try {
          await db.favoriteBooth.clear(); // お気に入りレコードをすべて入れ替え
          if (v) {
            await updateLastUpdated(v.lastUpdated ?? 0);
            await db.favoriteBooth.bulkAdd([...v.favoriteBoothMap.values()]);
          }
        }
        catch (e) {
          exception = e;
        }
        if (exception) {
          await db.delete(); // 例外になったらキャッシュデータベースは削除する
        }
      }
    },
    updateFavorite: async (fav: FavoriteBooth, lastUpdated: number) => {
      // State の更新 (computed の依存追跡のため新しい Map を作って差し替える)
      // state 未初期化 (setupFavorite 未実行など) でも IndexedDB と一致させるため空 Map から作る
      const favoriteBoothMap = new Map(state.value?.favoriteBoothMap);
      favoriteBoothMap.set(fav.boothId, fav);
      state.value = {
        lastUpdated: lastUpdated,
        favoriteBoothMap: favoriteBoothMap,
      };
      if (db !== undefined) {
        // IndexedDB の更新
        let exception: unknown | undefined = undefined;
        try {
          await updateLastUpdated(lastUpdated);
          await db.favoriteBooth.put(fav);
        }
        catch (e) {
          exception = e;
        }
        if (exception) {
          await db.delete(); // 例外になったらキャッシュデータベースは削除する
        }
      }
    },
    deleteFavorites: async (boothIds: number[], lastUpdated: number) => {
      // State の更新 (computed の依存追跡のため新しい Map を作って差し替える)
      // state 未初期化 (setupFavorite 未実行など) でも IndexedDB と一致させるため空 Map から作る
      const favoriteBoothMap = new Map(state.value?.favoriteBoothMap);
      for (const deleteBoothId of boothIds) {
        favoriteBoothMap.delete(deleteBoothId);
      }
      state.value = {
        lastUpdated: lastUpdated,
        favoriteBoothMap: favoriteBoothMap,
      };
      if (db !== undefined) {
        // IndexedDB の更新
        let exception: unknown | undefined = undefined;
        try {
          await updateLastUpdated(lastUpdated);
          for (const deleteBoothId of boothIds) {
            await db.favoriteBooth.delete(deleteBoothId);
          }
        }
        catch (e) {
          exception = e;
        }
        if (exception) {
          await db.delete(); // 例外になったらキャッシュデータベースは削除する
        }
      }
    },
    getDatabaseName: () => getDatabaseName(),
  });
};
